import { addToolBreadcrumb } from '@tunasong/models'
import {
  FORM_POOL,
  computeFormSessionStats,
  generateFormQuizQuestion,
  validateFormQuizAnswer,
} from '@tunasong/music-lib'
import type {
  FormEntry,
  FormKind,
  FormQuizQuestion,
  FormQuizResult,
  FormQuizVerdict,
} from '@tunasong/music-lib'
import { Button, useSearchParams } from '@tunasong/ui-lib'
import { useEffect, useRef, useState } from 'react'
import type { FC } from 'react'
import type { Section } from '../player/section-sequencer.js'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { FormExploreChart } from './form-explore-chart.js'
import { useFormPlayback } from './form-playback.js'
import { FormQuizFeedback } from './form-quiz-feedback.js'
import { FormQuizQuestionPanel } from './form-quiz-question.js'
import { parseFormKindParam, parseFormSubMode } from './form-visualizer-params.js'
import { FormVisualizerSummary } from './form-visualizer-summary.js'
import { FormVisualizerWelcome } from './form-visualizer-welcome.js'
import styles from './form-visualizer.module.css'

const QUESTIONS_PER_SESSION = 5
const TEMPO_BPM = 100
const AUTOPLAY_DELAY_MS = 250
const SECTION_HIGHLIGHT_CLEAR_PADDING_MS = 400
const SECTION_GAP_MS = 250 // matches section-sequencer's PRE_ROLL + POST_ROLL

type SessionState =
  | { phase: 'welcome'; preSubMode: 'explore' | 'quiz' | null; preForm: FormKind | null }
  | { phase: 'exploring'; form: FormEntry; activeSectionIndex: number | null; fullFormPlaying: boolean }
  | { phase: 'asking-quiz'; index: number; question: FormQuizQuestion; results: FormQuizResult[] }
  | {
      phase: 'feedback-quiz'
      index: number
      question: FormQuizQuestion
      verdict: FormQuizVerdict
      results: FormQuizResult[]
    }
  | { phase: 'done-quiz'; results: FormQuizResult[] }

const findFormByKind = (kind: FormKind): FormEntry => {
  const found = FORM_POOL.find(f => f.kind === kind)
  if (!found) {
    throw new Error(`FORM_POOL missing kind=${kind}`)
  }
  return found
}

const buildExplorePlaybackSections = (form: FormEntry, idx: number): readonly Section[] => {
  const out: Section[] = []
  if (idx > 0) {
    const prev = form.sections[idx - 1]
    const leadIn = prev.progression.slice(-1)
    if (leadIn.length > 0) {
      out.push({
        kind: prev.kind,
        index: idx - 1,
        progression: leadIn,
        measuresInSection: Math.max(1, prev.measuresInSection / prev.progression.length),
      })
    }
  }
  const current = form.sections[idx]
  out.push({
    kind: current.kind,
    index: idx,
    progression: current.progression,
    measuresInSection: current.measuresInSection,
  })
  return out
}

const buildQuizPlaybackSections = (q: FormQuizQuestion): readonly Section[] =>
  q.auditionSections.map((s, i) => ({
    kind: s.kind,
    index: i,
    progression: s.progression,
    measuresInSection: s.measuresInSection,
  }))

/**
 * Full-form playback sections — every section of the form, contiguous (no
 * lead-in chord). Mirrors `buildQuizPlaybackSections`. Spec §4.2.
 */
const buildFullFormSections = (form: FormEntry): readonly Section[] =>
  form.sections.map((s, i) => ({
    kind: s.kind,
    index: i,
    progression: s.progression,
    measuresInSection: s.measuresInSection,
  }))

/**
 * Form Visualizer v1 host. Two sub-modes (Form Explore, Form ID Quiz);
 * 5-question Quiz sessions; locked tempo 100 BPM, locked key C major. Mirrors
 * Rhythm Trainer's clone-per-sub-mode state machine.
 *
 * Spec: docs/superpowers/specs/2026-05-23-form-visualizer-v1-design.md §3
 */
export const FormVisualizer: FC = () => {
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<SessionState>(() => {
    const preSubMode = parseFormSubMode(searchParams.get('mode'))
    const preForm = parseFormKindParam(searchParams.get('form'))
    if (preSubMode === 'quiz') {
      return {
        phase: 'asking-quiz',
        index: 0,
        question: generateFormQuizQuestion(),
        results: [],
      }
    }
    if (preSubMode === 'explore' && preForm) {
      return { phase: 'exploring', form: findFormByKind(preForm), activeSectionIndex: null, fullFormPlaying: false }
    }
    return { phase: 'welcome', preSubMode, preForm }
  })

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'form-visualizer' })
  }, [])

  // Compute playback sections by phase. Null when no playback applies.
  let playbackSections: readonly Section[] | null = null
  if (state.phase === 'exploring' && state.fullFormPlaying) {
    playbackSections = buildFullFormSections(state.form)
  } else if (state.phase === 'exploring' && state.activeSectionIndex !== null) {
    playbackSections = buildExplorePlaybackSections(state.form, state.activeSectionIndex)
  } else if (state.phase === 'asking-quiz' || state.phase === 'feedback-quiz') {
    playbackSections = buildQuizPlaybackSections(state.question)
  }
  const { play, stop } = useFormPlayback({ sections: playbackSections, bpm: TEMPO_BPM })

  // Autoplay Quiz audition on every new asking-quiz mount.
  const askingQuestion = state.phase === 'asking-quiz' ? state.question : null
  useEffect(() => {
    if (askingQuestion === null) {
      return
    }
    const handle = window.setTimeout(play, AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [askingQuestion, play])

  // Full-form playback timer chain (Spec §4.3). Holds every scheduled handle so
  // all teardown paths (Stop, click-a-section, unmount) can cancel them.
  const fullFormTimers = useRef<number[]>([])
  const clearFullFormTimers = (): void => {
    fullFormTimers.current.forEach(window.clearTimeout)
    fullFormTimers.current = []
  }
  useEffect(() => () => clearFullFormTimers(), [])

  // Auto-clear the Explore active-section highlight once playback duration elapses.
  // Suppressed during full-form play — the timer chain owns the highlight then.
  const exploringActive =
    state.phase === 'exploring' && state.activeSectionIndex !== null ? state.activeSectionIndex : null
  const exploringFullForm = state.phase === 'exploring' && state.fullFormPlaying
  const clearTimeoutRef = useRef<number | null>(null)
  useEffect(() => {
    if (
      exploringActive === null ||
      playbackSections === null ||
      playbackSections.length === 0 ||
      exploringFullForm
    ) {
      return
    }
    const quarterMs = 60_000 / TEMPO_BPM
    const totalMs =
      playbackSections.reduce((acc, s) => acc + s.measuresInSection * 4 * quarterMs, 0) +
      (playbackSections.length - 1) * SECTION_GAP_MS +
      SECTION_HIGHLIGHT_CLEAR_PADDING_MS

    const handle = window.setTimeout(() => {
      setState(curr =>
        curr.phase === 'exploring' ? { ...curr, activeSectionIndex: null } : curr
      )
    }, totalMs)
    clearTimeoutRef.current = handle
    return () => window.clearTimeout(handle)
    // playbackSections is derived from state; safe to reference exploringActive alone.
  }, [exploringActive, playbackSections, exploringFullForm])

  const handleStartExplore = (formKind: FormKind): void => {
    addToolBreadcrumb('opened', { tool: 'form-visualizer', mode: 'explore' })
    setState({ phase: 'exploring', form: findFormByKind(formKind), activeSectionIndex: null, fullFormPlaying: false })
  }

  const handleStartQuiz = (): void => {
    addToolBreadcrumb('opened', { tool: 'form-visualizer', mode: 'quiz' })
    setState({
      phase: 'asking-quiz',
      index: 0,
      question: generateFormQuizQuestion(),
      results: [],
    })
  }

  const handleSectionClick = (sectionIndex: number): void => {
    if (state.phase !== 'exploring') {
      return
    }
    stop()
    clearFullFormTimers()
    if (clearTimeoutRef.current !== null) {
      window.clearTimeout(clearTimeoutRef.current)
      clearTimeoutRef.current = null
    }
    setState({ ...state, fullFormPlaying: false, activeSectionIndex: sectionIndex })
    // Schedule play in the next tick so the new playbackSections is wired.
    window.setTimeout(play, 0)
  }

  const handleAnswer = (picked: FormKind): void => {
    if (state.phase !== 'asking-quiz') {
      return
    }
    const verdict = validateFormQuizAnswer(state.question, picked)
    addToolBreadcrumb('answered', {
      tool: 'form-visualizer',
      subMode: 'quiz',
      correct: verdict.correct,
    })
    setState({
      phase: 'feedback-quiz',
      index: state.index,
      question: state.question,
      verdict,
      results: state.results,
    })
  }

  const handleNext = (): void => {
    if (state.phase !== 'feedback-quiz') {
      return
    }
    const results: FormQuizResult[] = [
      ...state.results,
      { question: state.question, verdict: state.verdict },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-quiz', results })
      return
    }
    setState({
      phase: 'asking-quiz',
      index: state.index + 1,
      question: generateFormQuizQuestion(),
      results,
    })
  }

  const handleRestart = (): void => {
    setState({
      phase: 'asking-quiz',
      index: 0,
      question: generateFormQuizQuestion(),
      results: [],
    })
  }

  const handleSwitchToExplore = (): void => {
    setState({ phase: 'welcome', preSubMode: 'explore', preForm: null })
  }

  const sectionDurationMs = (s: Section): number =>
    s.measuresInSection * 4 * (60_000 / TEMPO_BPM) + SECTION_GAP_MS

  const startFullForm = (): void => {
    if (state.phase !== 'exploring') {
      return
    }
    stop()
    clearFullFormTimers()
    if (clearTimeoutRef.current !== null) {
      window.clearTimeout(clearTimeoutRef.current)
      clearTimeoutRef.current = null
    }
    const sections = buildFullFormSections(state.form)
    setState({ ...state, fullFormPlaying: true, activeSectionIndex: 0 })
    window.setTimeout(play, 0)

    let offset = 0
    sections.forEach((s, i) => {
      if (i > 0) {
        offset += sectionDurationMs(sections[i - 1])
        fullFormTimers.current.push(
          window.setTimeout(
            () => setState(c => (c.phase === 'exploring' ? { ...c, activeSectionIndex: i } : c)),
            offset
          )
        )
      }
    })
    const totalMs =
      sections.reduce((a, s) => a + sectionDurationMs(s), 0) + SECTION_HIGHLIGHT_CLEAR_PADDING_MS
    fullFormTimers.current.push(
      window.setTimeout(
        () =>
          setState(c =>
            c.phase === 'exploring' ? { ...c, fullFormPlaying: false, activeSectionIndex: null } : c
          ),
        totalMs
      )
    )
  }

  const stopFullForm = (): void => {
    stop()
    clearFullFormTimers()
    setState(c =>
      c.phase === 'exploring' ? { ...c, fullFormPlaying: false, activeSectionIndex: null } : c
    )
  }

  const handleToggleFullForm = (): void => {
    if (state.phase !== 'exploring') {
      return
    }
    if (state.fullFormPlaying) {
      stopFullForm()
    } else {
      startFullForm()
    }
  }

  const footerTools: RelatedTool[] = [
    { targetRoute: '/app/cadences', lexiconId: 'crosslink-form-to-cadence' },
    { targetRoute: '/app/rhythm', lexiconId: 'crosslink-form-to-rhythm' },
    { targetRoute: '/music-theory', lexiconId: 'crosslink-form-to-suite' },
  ]

  if (state.phase === 'welcome') {
    return (
      <div className={styles.container}>
        <FormVisualizerWelcome
          preSelectedSubMode={state.preSubMode}
          preSelectedForm={state.preForm}
          onStartExplore={handleStartExplore}
          onStartQuiz={handleStartQuiz}
        />
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  if (state.phase === 'exploring') {
    return (
      <div className={styles.container}>
        <div className={styles.exploreToolbar}>
          <Button
            type="button"
            variant="outlined"
            size="small"
            aria-pressed={state.fullFormPlaying}
            aria-label={state.fullFormPlaying ? 'Stop' : 'Play whole form'}
            onClick={handleToggleFullForm}
          >
            {state.fullFormPlaying ? '◼ Stop' : '▶ Play whole form'}
          </Button>
        </div>
        <FormExploreChart
          form={state.form}
          activeSectionIndex={state.activeSectionIndex}
          onSectionClick={handleSectionClick}
        />
      </div>
    )
  }

  if (state.phase === 'asking-quiz') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length
    return (
      <div className={styles.container}>
        <FormQuizQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          interactive={true}
          onHearIt={play}
          onAnswer={handleAnswer}
        />
      </div>
    )
  }

  if (state.phase === 'feedback-quiz') {
    const scoreCorrect =
      state.results.filter(r => r.verdict.correct).length + (state.verdict.correct ? 1 : 0)
    return (
      <div className={styles.container}>
        <FormQuizQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length + 1}
          interactive={false}
          onHearIt={play}
          onAnswer={() => {
            /* feedback phase — disabled via interactive=false */
          }}
        />
        <FormQuizFeedback
          question={state.question}
          verdict={state.verdict}
          onHearAgain={play}
          onNext={handleNext}
        />
      </div>
    )
  }

  // state.phase === 'done-quiz'
  return (
    <div className={styles.container}>
      <FormVisualizerSummary
        stats={computeFormSessionStats(state.results)}
        onRestart={handleRestart}
        onSwitchToExplore={handleSwitchToExplore}
      />
    </div>
  )
}
